import { useTranslation } from 'react-i18next';
import {
  Form,
  Button,
  Select,
  Input,
  Row,
  Col,
  Segmented,
  App,
  Flex,
  FormProps
} from 'antd';
import {
  DURATION_TYPE,
  DURATION_TYPE_DATA,
  LIMIT_TYPE_DATA,
  UNIT_OPTIONS,
  UNIT_OPTIONS_DATA
} from 'constants/subscriptions';
import { useEffect, useState } from 'react';
import { LANGUAGES } from 'constants/languages';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createFeatureItem,
  fetchFeatureItemById,
  updateFeatureItem
} from 'services/api/subscriptions';
import {
  FeatureItemInput,
  SubscriptionDurationType,
  SubscriptionLimitType
} from 'types/api/subscriptions';

import { PageWrapper } from 'components/ui';
import { getInputDir } from 'utils/helpers';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';
import { useSubscriptionFeatures } from '../hooks';

type OptionType = {
  limit: string;
  name: Record<string, string>;
  type: SubscriptionLimitType;
};

type ValuesType = {
  subscription_feature_id: string;
  unit: string;
  option_1: OptionType;
  option_2: OptionType;
  option_3: OptionType;
};

const SubscriptionFeaturesDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [duration, setDuration] = useState<SubscriptionDurationType>(
    DURATION_TYPE.month
  );

  const [loading, setLoading] = useState(!!id);
  const [isSaving, setIsSaving] = useState(false);

  const features = useSubscriptionFeatures();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const featureItem = await fetchFeatureItemById(id);
        if (cancelled || !featureItem) return;

        const optionValues = featureItem.options.reduce((acc, item) => {
          if (item && item.duration_type && item.name) {
            const { duration_type, limit, name, type } = item;

            acc[`option_${duration_type}`] = {
              name,
              limit,
              type
            };
          }

          return acc;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }, {} as Record<string, any>);

        form.setFieldsValue({
          subscription_feature_id: featureItem.subscription_feature_id,
          unit: featureItem.unit,
          ...optionValues
        });
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const unitValue = Form.useWatch('unit', form);

  const isHiddenOptions = () => {
    if (!unitValue) {
      return true;
    }

    switch (unitValue) {
      case UNIT_OPTIONS.has_access:
      case UNIT_OPTIONS.no_access:
        return true;
      default:
        return false;
    }
  };

  const onFinish = async (values: ValuesType) => {
    const { option_1, option_2, option_3, ...rest } = values;
    const hideOptions = isHiddenOptions();

    const formattedOption = [
      {
        ...option_1,
        limit: Number(option_1.limit) || 0,
        duration_type: DURATION_TYPE.month
      },
      {
        ...option_2,
        limit: Number(option_2.limit) || 0,
        duration_type: DURATION_TYPE.year
      },
      {
        ...option_3,
        limit: Number(option_3.limit) || 0,
        duration_type: DURATION_TYPE.free
      }
    ];

    const input: FeatureItemInput = {
      subscription_feature_id: Number(rest.subscription_feature_id),
      unit: Number(rest.unit),
      value: 0,
      ...(hideOptions ? {} : { options: formattedOption })
    };

    try {
      setIsSaving(true);

      if (id) {
        await updateFeatureItem(id, input);
        modal.success({
          title: t('messages.success'),
          content: t('subscription.update_feature_success')
        });
      } else {
        await createFeatureItem(input);
        modal.success({
          title: t('messages.success'),
          content: t('subscription.create_feature_success')
        });
      }

      navigate('/subscriptions/features');
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Form options are indexed (option_1/2/3) but the wire duration_type
  // is the canonical SubscriptionDurationType. Map the form-index back
  // to the enum value on error focus.
  const DURATION_BY_OPTION_INDEX: Record<number, SubscriptionDurationType> = {
    1: SubscriptionDurationType.Month,
    2: SubscriptionDurationType.Year,
    3: SubscriptionDurationType.Free
  };

  const onFinishFailed: FormProps['onFinishFailed'] = e => {
    const firstErrorName = e.errorFields?.[0]?.name?.[0];
    const errorIndex =
      typeof firstErrorName === 'string' && firstErrorName?.includes('option_')
        ? +firstErrorName.replace('option_', '')
        : -1;

    const next = DURATION_BY_OPTION_INDEX[errorIndex];

    if (next) {
      setDuration(next);
    }
  };

  const onDurationChange = (newDuration: SubscriptionDurationType) => {
    setDuration(newDuration);
  };

  return (
    <PageWrapper
      back
      loading={loading}
      color="white"
      title={t(`subscription.${id ? 'update' : 'create'}_subscription_feature`)}
      extra={[
        <Button
          key="submit-button"
          disabled={isSaving}
          loading={isSaving}
          type="primary"
          onClick={() => form.submit()}
          style={{ width: 150 }}
        >
          {t('common.save')}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="subscription_feature_id"
              label={t('subscription.select_feature')}
              rules={[{ required: true }]}
            >
              <Select
                size="large"
                placeholder={t('subscription.select_feature')}
              >
                {features.map(item => (
                  <Select.Option
                    key={`feature-item-${item.id}`}
                    value={item.id}
                  >
                    <Flex gap={8} align="center">
                      <img
                        src={getFileUrl(item.icon)}
                        alt={item.name}
                        style={{ objectFit: 'contain', width: 20, height: 20 }}
                      />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: item.name.replace('<br />', '')
                        }}
                      />
                    </Flex>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="unit"
              label={t('subscription.set_measurement_unit')}
              rules={[{ required: true }]}
            >
              <Select size="large" placeholder={t('subscription.set_unit')}>
                {UNIT_OPTIONS_DATA.map(item => (
                  <Select.Option
                    key={`unit-option-${item.label}`}
                    value={item.value}
                  >
                    {t(`subscription.${item.label}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {!isHiddenOptions() && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Segmented
                options={DURATION_TYPE_DATA.map(item => ({
                  ...item,
                  label: t(`subscription.${item.label}`)
                }))}
                block
                size="large"
                value={duration}
                onChange={e => onDurationChange(e as SubscriptionDurationType)}
              />
            </Col>
            <Col span={24}>
              {DURATION_TYPE_DATA.map(item => {
                return (
                  <div
                    style={{
                      display: item.value === duration ? 'block' : 'none'
                    }}
                    key={`form-${item.value}`}
                  >
                    <Row gutter={[16, 0]}>
                      {LANGUAGES.map(lang => (
                        <Col span={12} key={`${item.value}-${lang.key}`}>
                          <Form.Item
                            name={[`option_${item.value}`, 'name', lang.locale]}
                            label={`Feature value in ${lang.label}`}
                          >
                            <Input
                              size="large"
                              dir={getInputDir(lang.locale)}
                            />
                          </Form.Item>
                        </Col>
                      ))}
                      <Col span={12}>
                        <Form.Item
                          name={[`option_${item.value}`, 'type']}
                          label={t('subscription.limit_type')}
                          rules={[{ required: true }]}
                        >
                          <Select
                            size="large"
                            placeholder={t('subscription.set_limit')}
                          >
                            {LIMIT_TYPE_DATA.map(item => (
                              <Select.Option
                                key={`unit-option-${item.label}`}
                                value={item.value}
                              >
                                {t(`subscription.${item.label}`)}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={[`option_${item.value}`, 'limit']}
                          label={t('subscription.limit')}
                          rules={[
                            {
                              required: false,
                              pattern: /^\d+(\.\d+)?$/,
                              message: 'Please enter a valid number'
                            }
                          ]}
                        >
                          <Input size="large" min={1} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </Col>
          </Row>
        )}
      </Form>
    </PageWrapper>
  );
};

export default SubscriptionFeaturesDetails;
